import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Clock, CheckSquare, Target, Zap, AlarmClock, Tag } from 'lucide-react';
import Confetti from 'react-confetti';
import { Progress } from '@/components/ui/progress';

const CATEGORIES = [
  { value: 'saude',         label: 'Saúde',         emoji: '🏥' },
  { value: 'produtividade', label: 'Produtividade', emoji: '💼' },
  { value: 'bem-estar',     label: 'Bem-estar',     emoji: '🧘' },
  { value: 'fitness',       label: 'Fitness',       emoji: '💪' },
  { value: 'educacao',      label: 'Educação',      emoji: '📚' },
  { value: 'social',        label: 'Social',        emoji: '🤝' },
  { value: 'financas',      label: 'Finanças',      emoji: '💰' },
  { value: 'espiritualidade',label: 'Espiritualidade', emoji: '✨' },
];

const FREQUENCIES = [
  { value: 'daily',       label: 'Diário' },
  { value: 'weekdays',    label: 'Dias úteis' },
  { value: 'weekends',    label: 'Fins de semana' },
  { value: '3x_week',     label: '3× por semana' },
  { value: 'weekly',      label: 'Semanal' },
  { value: 'custom',      label: 'Personalizado' },
];

const DIFFICULTIES = [
  { value: 'easy',   label: 'Fácil',  color: 'text-emerald-400' },
  { value: 'medium', label: 'Médio',  color: 'text-yellow-400'  },
  { value: 'hard',   label: 'Difícil', color: 'text-red-400'    },
];

const COLORS = [
  '#34d399','#10b981','#3b82f6','#8b5cf6',
  '#f59e0b','#ef4444','#ec4899','#06b6d4',
];

const ICONS = [
  { value: 'check',   emoji: '✅' },
  { value: 'fire',    emoji: '🔥' },
  { value: 'book',    emoji: '📚' },
  { value: 'run',     emoji: '🏃' },
  { value: 'water',   emoji: '💧' },
  { value: 'sleep',   emoji: '😴' },
  { value: 'food',    emoji: '🥦' },
  { value: 'mind',    emoji: '🧠' },
  { value: 'music',   emoji: '🎵' },
  { value: 'money',   emoji: '💰' },
  { value: 'heart',   emoji: '❤️' },
  { value: 'star',    emoji: '⭐' },
];

const DAYS = [
  { key: 'mon', label: 'S' },
  { key: 'tue', label: 'T' },
  { key: 'wed', label: 'Q' },
  { key: 'thu', label: 'Q' },
  { key: 'fri', label: 'S' },
  { key: 'sat', label: 'S' },
  { key: 'sun', label: 'D' },
];

const DEFAULT_FORM = {
  name: '',
  description: '',
  schedule_time: '08:00',
  duration_minutes: 15,
  frequency: 'daily',
  custom_days: [],
  category: 'saude',
  difficulty: 'easy',
  color: '#34d399',
  icon: 'check',
  reminder_enabled: true,
  reminder_minutes_before: 10,
  goal_streak: 21,
  xp_reward: 10,
  tags: '',
  notes: '',
};

export default function HabitsPage() {
  const [habits, setHabits]       = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [editHabit, setEditHabit] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('todas');
  const [form, setForm] = useState(DEFAULT_FORM);

  const today = new Date().toISOString().split('T')[0];

  const fetchData = useCallback(async () => {
    try {
      const [habitsRes, logsRes] = await Promise.all([
        api.get('/habits'),
        api.get(`/habits/logs?date=${today}`),
      ]);
      setHabits(habitsRes.data.filter(h => h.active));
      setHabitLogs(logsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      custom_days: f.custom_days.includes(day)
        ? f.custom_days.filter(d => d !== day)
        : [...f.custom_days, day],
    }));
  };

  const toggleHabit = async (habitId) => {
    try {
      const res = await api.post(`/habits/${habitId}/toggle?date=${today}`);
      setHabitLogs(prev => {
        const existing = prev.find(l => l.habit_id === habitId);
        if (existing) return prev.map(l => l.habit_id === habitId ? { ...l, completed: res.data.completed } : l);
        return [...prev, { habit_id: habitId, date: today, completed: res.data.completed }];
      });
      if (res.data.completed) {
        const allDone = habits.every(h =>
          h.id === habitId ? true : habitLogs.find(l => l.habit_id === h.id)?.completed
        );
        if (allDone && habits.length > 1) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
          toast.success('Todos os hábitos concluídos hoje! Incrível! 🔥');
        } else {
          toast.success(`+${form.xp_reward || 10} XP`);
        }
      }
    } catch (err) { console.error(err); }
  };

  const isHabitDone = (habitId) =>
    habitLogs.find(l => l.habit_id === habitId)?.completed || false;

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome do hábito'); return; }
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      if (editHabit) {
        await api.put(`/habits/${editHabit}`, payload);
        toast.success('Hábito atualizado!');
      } else {
        await api.post('/habits', payload);
        toast.success('Hábito criado!');
      }
      setShowAdd(false);
      setEditHabit(null);
      setForm(DEFAULT_FORM);
      fetchData();
    } catch (err) { toast.error('Erro ao salvar'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/habits/${id}`);
      toast.success('Hábito removido');
      fetchData();
    } catch (err) { toast.error('Erro ao remover'); }
  };

  const openEdit = (habit) => {
    setForm({
      ...DEFAULT_FORM,
      ...habit,
      tags: Array.isArray(habit.tags) ? habit.tags.join(', ') : (habit.tags || ''),
      custom_days: habit.custom_days || [],
    });
    setEditHabit(habit.id);
    setShowAdd(true);
  };

  const completedToday   = habits.filter(h => isHabitDone(h.id)).length;
  const totalHabits      = habits.length;
  const completionPct    = totalHabits > 0 ? Math.round((completedToday / totalHabits) * 100) : 0;
  const filteredHabits   = habits.filter(h => categoryFilter === 'todas' || h.category === categoryFilter);
  const sortedHabits     = [...filteredHabits].sort((a, b) => {
    const aDone = isHabitDone(a.id), bDone = isHabitDone(b.id);
    if (aDone !== bDone) return aDone ? 1 : -1;
    return a.schedule_time.localeCompare(b.schedule_time);
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[1,2,3].map(i => <div key={i} className="h-20 bg-muted rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="habits-page">
      {showConfetti && <Confetti recycle={false} numberOfPieces={220} />}

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-fluid-2xl font-heading font-bold tracking-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}>Hábitos</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {completedToday}/{totalHabits} concluídos hoje
          </p>
          {totalHabits > 0 && (
            <Progress value={completionPct} className="h-1.5 mt-2 max-w-xs" />
          )}
        </div>
        <Button
          onClick={() => { setEditHabit(null); setForm(DEFAULT_FORM); setShowAdd(true); }}
          className="rounded-full touch-manipulation"
          data-testid="add-habit-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Novo Hábito
        </Button>
      </div>

      {habits.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <CheckSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Adicione seu primeiro hábito positivo</p>
            <p className="text-sm text-muted-foreground mt-1">Pequenos passos, grandes resultados.</p>
            <Button onClick={() => setShowAdd(true)} className="mt-4 rounded-full" data-testid="add-habit-empty-btn">
              <Plus className="w-4 h-4 mr-1" /> Criar hábito
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Category filter chips */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategoryFilter('todas')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors touch-manipulation ${
                categoryFilter === 'todas'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'
              }`}
            >Todas</button>
            {CATEGORIES.map(c => (
              <button key={c.value}
                onClick={() => setCategoryFilter(c.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors touch-manipulation ${
                  categoryFilter === c.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'
                }`}
              >{c.emoji} {c.label}</button>
            ))}
          </div>

          {/* Habit cards */}
          <div className="space-y-3 stagger-children">
            {sortedHabits.map((habit) => {
              const done = isHabitDone(habit.id);
              const diffObj = DIFFICULTIES.find(d => d.value === habit.difficulty);
              const catObj  = CATEGORIES.find(c => c.value === habit.category);
              return (
                <Card key={habit.id}
                  className={`transition-opacity duration-200 ${done ? 'opacity-60' : ''}`}
                  data-testid={`habit-card-${habit.id}`}
                >
                  <CardContent className="py-3.5 px-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 cursor-pointer" onClick={() => toggleHabit(habit.id)}
                        data-testid={`toggle-habit-${habit.id}`}>
                        <Checkbox checked={done} className="pointer-events-none h-5 w-5" />
                      </div>
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5"
                        style={{ backgroundColor: habit.color }} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium leading-snug ${
                          done ? 'line-through text-muted-foreground' : ''
                        }`}>
                          {ICONS.find(i => i.value === habit.icon)?.emoji} {habit.name}
                        </p>
                        {habit.description && (
                          <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">
                            {habit.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                            <Clock className="w-3 h-3" /> {habit.schedule_time}
                          </span>
                          {habit.duration_minutes && (
                            <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                              <AlarmClock className="w-3 h-3" /> {habit.duration_minutes}min
                            </span>
                          )}
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {catObj?.emoji} {catObj?.label || habit.category}
                          </Badge>
                          {diffObj && (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${diffObj.color}`}>
                              {diffObj.label}
                            </Badge>
                          )}
                          {habit.goal_streak > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-400">
                              <Target className="w-2.5 h-2.5 mr-0.5" />{habit.goal_streak}d
                            </Badge>
                          )}
                          {habit.xp_reward > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-yellow-400">
                              <Zap className="w-2.5 h-2.5 mr-0.5" />+{habit.xp_reward} XP
                            </Badge>
                          )}
                        </div>
                        {Array.isArray(habit.tags) && habit.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {habit.tags.map(tag => (
                              <span key={tag}
                                className="text-[10px] px-1.5 py-0 rounded bg-muted text-muted-foreground">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8 touch-manipulation"
                          onClick={() => openEdit(habit)} data-testid={`edit-habit-${habit.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive-foreground touch-manipulation"
                          onClick={() => handleDelete(habit.id)} data-testid={`delete-habit-${habit.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={v => { setShowAdd(v); if (!v) setEditHabit(null); }}>
        <DialogContent
          className="rounded-2xl max-w-lg w-full max-h-[90svh] overflow-y-auto"
          data-testid="habit-dialog"
        >
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'DM Serif Display', serif" }}>
              {editHabit ? 'Editar Hábito' : 'Novo Hábito'}
            </DialogTitle>
            <DialogDescription>Configure cada detalhe do seu hábito</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-1">

            {/* ─ IDENTIDADE ─────────────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Identidade
              </p>
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input data-testid="habit-name-input"
                  placeholder="Ex: Meditar 10min, Lêr 20 páginas..."
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label>Descrição <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                <Textarea data-testid="habit-desc-input"
                  placeholder="Por que esse hábito importa para você?"
                  value={form.description}
                  onChange={e => setField('description', e.target.value)}
                  className="rounded-xl text-sm resize-none min-h-[72px]" />
              </div>
            </section>

            {/* ─ ÍCONE & COR ─────────────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Ícone & Cor
              </p>
              <div className="space-y-2">
                <Label>Ícone</Label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(ic => (
                    <button key={ic.value}
                      type="button"
                      onClick={() => setField('icon', ic.value)}
                      className={`w-9 h-9 rounded-xl text-lg flex items-center justify-center
                        transition-all touch-manipulation
                        ${ form.icon === ic.value
                          ? 'ring-2 ring-primary bg-primary/10'
                          : 'bg-muted/50 hover:bg-muted' }`}
                    >{ic.emoji}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cor</Label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} type="button"
                      onClick={() => setField('color', c)}
                      className={`w-8 h-8 rounded-full transition-all touch-manipulation ${
                        form.color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* ─ AGENDA ────────────────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Agenda
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Horário</Label>
                  <Input data-testid="habit-time-input" type="time"
                    value={form.schedule_time}
                    onChange={e => setField('schedule_time', e.target.value)}
                    className="rounded-xl h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Duração (min)</Label>
                  <Input data-testid="habit-duration-input" type="number" min={1} max={480}
                    value={form.duration_minutes}
                    onChange={e => setField('duration_minutes', Number(e.target.value))}
                    className="rounded-xl h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Frequência</Label>
                <Select value={form.frequency} onValueChange={v => setField('frequency', v)}>
                  <SelectTrigger className="rounded-xl h-11" data-testid="habit-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === 'custom' && (
                <div className="space-y-2">
                  <Label>Dias da semana</Label>
                  <div className="flex gap-2">
                    {DAYS.map(d => (
                      <button key={d.key} type="button"
                        onClick={() => toggleDay(d.key)}
                        className={`w-9 h-9 rounded-full text-xs font-semibold
                          transition-colors touch-manipulation
                          ${ form.custom_days.includes(d.key)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80' }`}
                      >{d.label}</button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* ─ CATEGORIA & DIFICULDADE ──────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Classificação
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={v => setField('category', v)}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="habit-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.emoji} {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Dificuldade</Label>
                  <Select value={form.difficulty} onValueChange={v => setField('difficulty', v)}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="habit-difficulty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* ─ GAMIFICAÇÃO ────────────────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Gamificação
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Target className="w-3.5 h-3.5" /> Meta de streak (dias)
                  </Label>
                  <Input type="number" min={1} max={365}
                    value={form.goal_streak}
                    onChange={e => setField('goal_streak', Number(e.target.value))}
                    className="rounded-xl h-11"
                    data-testid="habit-streak-goal-input" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5" /> XP por conclusão
                  </Label>
                  <Input type="number" min={1} max={500}
                    value={form.xp_reward}
                    onChange={e => setField('xp_reward', Number(e.target.value))}
                    className="rounded-xl h-11"
                    data-testid="habit-xp-input" />
                </div>
              </div>
            </section>

            {/* ─ LEMBRETE ──────────────────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Lembrete
              </p>
              <div className="flex items-center justify-between">
                <Label htmlFor="reminder-sw">Ativar lembrete</Label>
                <Switch
                  id="reminder-sw"
                  checked={form.reminder_enabled}
                  onCheckedChange={v => setField('reminder_enabled', v)}
                  data-testid="habit-reminder-switch"
                />
              </div>
              {form.reminder_enabled && (
                <div className="space-y-2">
                  <Label>Antecedência: {form.reminder_minutes_before} min antes</Label>
                  <Slider
                    min={1} max={60} step={1}
                    value={[form.reminder_minutes_before]}
                    onValueChange={([v]) => setField('reminder_minutes_before', v)}
                    className="w-full"
                    data-testid="habit-reminder-slider"
                  />
                </div>
              )}
            </section>

            {/* ─ TAGS & NOTAS ──────────────────────── */}
            <section className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Extras
              </p>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5" /> Tags <span className="text-muted-foreground font-normal">(separadas por vírgula)</span>
                </Label>
                <Input placeholder="manha, rotina, foco..."
                  value={form.tags}
                  onChange={e => setField('tags', e.target.value)}
                  className="rounded-xl h-11"
                  data-testid="habit-tags-input" />
              </div>
              <div className="space-y-2">
                <Label>Notas internas <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                <Textarea placeholder="Dicas, contexto, motivação..."
                  value={form.notes}
                  onChange={e => setField('notes', e.target.value)}
                  className="rounded-xl text-sm resize-none min-h-[64px]"
                  data-testid="habit-notes-input" />
              </div>
            </section>

          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditHabit(null); }}
              className="rounded-xl flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="rounded-xl flex-1" data-testid="save-habit-btn">
              {editHabit ? 'Salvar alterações' : 'Criar hábito'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
